import {
  LESSON_CONTENT_MODEL_BY_TYPE,
  LESSON_CONTENT_TYPES,
} from '@/dictionary';

/**
 * Хелперы состава урока.
 *
 * API отдаёт материалы плоским списком внутри параграфа, а типы материалов —
 * массивом строк (`material_types`). Названия и порядок иконок берём из словаря,
 * счётчики считаем на месте из самих материалов.
 */

/** Приводит model_type материала к типу вкладки: videolecture -> videomaterial */
export const getLessonMaterialModel = (modelType) =>
  LESSON_CONTENT_MODEL_BY_TYPE[modelType] ?? modelType;

/**
 * Тип материала так, как его знает урок (`highlightables.highlightable_type`).
 *
 * У части сущностей настоящий тип лежит в `model_subtype`: запись энциклопедии
 * приходит как article + wiki, региональные материалы — как article +
 * region_*. Лекции же приходят под своими моделями, их приводим к типу вкладки.
 */
export const getLessonMaterialType = (material) =>
  getLessonMaterialModel(material?.model_subtype ?? material?.model_type);

/** Уникальный ключ материала в пределах параграфа: тип + id */
export const getLessonMaterialKey = (material) =>
  `${getLessonMaterialType(material)}:${material?.id}`;

/** Типы материалов списком, в порядке словаря */
export const getLessonMaterialTypes = (materials = []) => {
  const models = new Set(materials.map(getLessonMaterialType));

  return LESSON_CONTENT_TYPES.filter(({ model }) => models.has(model)).map(
    ({ model }) => model,
  );
};

/** Название типа материала для подсказки на иконке */
export const getLessonMaterialTypeName = (model) =>
  LESSON_CONTENT_TYPES.find((type) => type.model === model)?.name ?? model;

/**
 * Материал в формате запроса. `material_type` принимает только значения из
 * `LessonItem::MATERIAL_TYPES`, поэтому тип карточки приводим к типу урока.
 */
export const toLessonMaterialPayload = (material, subject, paragraph) => ({
  material_type: getLessonMaterialType(material),
  material_id: material?.id,
  subject_id: subject?.id,
  highlight_id: paragraph?.id,
});

/**
 * Состав урока в формате запроса: PATCH и POST принимают материалы
 * плоским списком с указанием учебника и параграфа.
 */
export const getLessonMaterialsPayload = (lesson) =>
  (lesson?.subjects ?? []).flatMap((subject) =>
    (subject.paragraphs ?? []).flatMap((paragraph) =>
      (paragraph.materials ?? []).map((material) =>
        toLessonMaterialPayload(material, subject, paragraph),
      ),
    ),
  );

/** Убирает повторы — бэкенд их не блокирует и завышает items_count */
export const dedupeLessonMaterials = (materials) => {
  const seen = new Set();

  return materials.filter((material) => {
    const key = [
      material.material_type,
      material.material_id,
      material.subject_id,
      material.highlight_id,
    ].join(':');

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};
